import React, {useCallback, useEffect, useRef, useState} from 'react'
import axios from 'axios'
import moment from 'moment'
import {KTIcon, toAbsoluteUrl} from '../../../_metronic/helpers'
import {checkError} from '../../modules/globallycommanFunctions/commanfunction'
import {useAuth} from '../../modules/auth'

type ActivityUser = {
  id: number
  name: string
  email: string
  avatar: string | null
  role: string
}

type ActivityLog = {
  id: number
  activity: string
  action: string
  status: string
  type: string
  request_type?: string
  timestamp: string
  user: ActivityUser
  user_type_id?: number
}

const API_URL:any = process.env.REACT_APP_KT_API_URL
const PAGE_LIMIT = 20

const BASE_URL = API_URL.replace(/\/api$/, '')

const ActivityLogWidget = () => {
  const {logout, currentUser} = useAuth()
  const user_type_id = currentUser?.user_type?.id
  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)

  const fetchActivity = useCallback(async (pageToFetch: number) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const tokenString: any = localStorage.getItem('kt-auth-react-v')
      const token = tokenString ? JSON.parse(tokenString) : null
      const response = await axios.get(`${API_URL}/allProducts/logs/activity`, {
        params: {page: pageToFetch, limit: PAGE_LIMIT, user_type_id},
        headers: {
          Authorization: `Bearer ${token?.access_token}`,
          'Content-Type': 'application/json',
        },
      })
      const rows: ActivityLog[] = response?.data?.data?.data ?? []
      const meta = response?.data?.data?.meta

      setLogs((prev) => (pageToFetch === 1 ? rows : [...prev, ...rows]))
      if (meta?.totalItems !== undefined) {
        setTotalCount(meta.totalItems)
      } else if (pageToFetch === 1) {
        setTotalCount(rows.length)
      }
      if (meta?.totalPages !== undefined) {
        setHasMore(pageToFetch < meta.totalPages)
      } else {
        setHasMore(rows.length >= PAGE_LIMIT)
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        checkError(error.response.status, logout)
      }
      setHasMore(false)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [logout, user_type_id])

  useEffect(() => {
    fetchActivity(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el || loadingRef.current || !hasMore) return
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80
    if (nearBottom) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchActivity(nextPage)
    }
  }, [page, hasMore, fetchActivity])

  return (
    <div className='card mt-8'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title d-flex align-items-center'>
          <div className='symbol symbol-40px me-3'>
            <span className='symbol-label bg-light-primary'>
              <KTIcon iconName='pulse' className='fs-2 text-primary' />
            </span>
          </div>
          <h3 className='fw-bold m-0 me-3'>Activity</h3>
          <span className='badge badge-light-primary fs-7 fw-semibold'>
            {(totalCount || logs.length).toLocaleString()}
          </span>
        </div>
      </div>
      <div className='card-body pt-0'>
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{maxHeight: '520px', overflowY: 'auto'}}
        >
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead className='sticky-top bg-body'>
              <tr className='fw-bold text-muted'>
                <th className='min-w-200px'>Users</th>
                <th className='min-w-300px'>Activity</th>
                <th className='min-w-180px'>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-circle symbol-45px overflow-hidden me-3'>
                        <div className='symbol-label'>
                          <img
                            src={
                              log.request_type !== 'cron' && log.user?.avatar
                                ? `${BASE_URL}/uploads/profile-pictures/${log.user.avatar}`
                                : toAbsoluteUrl('/media/avatars/blank.png')
                            }
                            alt={log.user?.name}
                            className='w-100'
                          />
                        </div>
                      </div>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-900 fw-bold mb-1'>{log.request_type === 'cron' ? 'Cron' : log.user?.name}</span>
                        <span className='text-muted text-gray-600'>{log.request_type === 'cron' ? 'System' : log.user?.role}</span>
                      </div>
                    </div>
                  </td>
                  <td className='text-muted text-gray-600' style={{whiteSpace: 'pre-line'}}>
                    {log.activity}
                  </td>
                  <td className='text-muted text-gray-600'>
                    {moment(log.timestamp).format('MM/DD/YYYY hh:mm:ss A')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && (
            <div className='text-center py-4 text-muted'>Loading...</div>
          )}
          {!loading && !hasMore && logs.length > 0 && (
            <div className='text-center py-4 text-muted'>No more activity</div>
          )}
          {!loading && logs.length === 0 && (
            <div className='text-center py-4 text-muted'>No activity found</div>
          )}
        </div>
      </div>
    </div>
  )
}

export {ActivityLogWidget}
